import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';
import classNames from 'classnames';
import moment from 'moment';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';
import ImageOutlineIcon from 'mdi-react/ImageOutlineIcon';
import CalendarMonthIcon from 'mdi-react/CalendarMonthIcon';
import TableStatusFilterComponent from '../../../../shared/components/form/TableFilterComponent';
import TableHeaderLabel from '../../../../shared/components/table/TableHeaderLabel';
import CommCopyHistoryBlockedPrompt from '../../../../shared/components/table/CommCopyHistoryBlockedPrompt';
import config, { appointmentStatus } from '../../../../config/app.config';
import { Link } from 'react-router-dom';
import { SortingField } from '../../../common/SortingCell';
import { getAppointmentDisplayStatus } from '../../../../utils/getAppointmentDisplayStatus';
import { getAppointmentLabels } from '../../../App/customize/hooks/useAppointmentLabels';
import { handleRequest } from '../../../../redux/services/APIService';
import {
  buildStatusUpdateArchiveHeader,
  buildStatusUpdateArchiveTranscript,
} from '../../../../utils/buildStatusUpdateArchiveTranscript';
import { StayPhotosModal } from '../../../modals/StayPhotos/StayPhotosModal';

const COPIED_BADGE_DURATION_MS = 1500;

function formatUpdatesMetaCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) {
    return '0';
  }
  return n > 99 ? '99+' : String(n);
}

function isBoardingStayType(row) {
  const typeTitle = String(row?.typeTitle || row?.type || '')
    .trim()
    .toLowerCase();
  return typeTitle === 'boarding stay' || typeTitle === 'boarding';
}

/** Same end-day math as Photo Wall: start + (boardingDurationDays - 1). */
function getEndDateCellDisplay(row) {
  if (!isBoardingStayType(row)) {
    return '\u2014';
  }

  if (
    row.status === appointmentStatus.upcoming ||
    row.status === appointmentStatus.ongoing
  ) {
    return 'ongoing';
  }

  const durationDays = Number(row.boardingDurationDays);
  if (!Number.isFinite(durationDays) || durationDays < 1) {
    return '\u2014';
  }

  const start = moment(row.dateTime, 'MM/DD/YYYY', true);
  if (!start.isValid()) {
    return '\u2014';
  }

  return start.clone().add(durationDays - 1, 'days').format('MM/DD/YYYY');
}

export default class TableComponent extends Component {
  static propTypes = {
    heads: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        name: PropTypes.string,
        sortable: PropTypes.bool,
        filterable: PropTypes.bool,
        link: PropTypes.func,
        headerTooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
        headerTooltipClassName: PropTypes.string,
        className: PropTypes.string,
        capitalize: PropTypes.bool,
        filterValue: PropTypes.array,
      }),
    ).isRequired,
    rows: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    isBoardingOnly: PropTypes.bool,
    facilityUtcOffset: PropTypes.number,
    overflowX: PropTypes.bool,
    showActions: PropTypes.bool,
    updatesColumnMode: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      copiedAppointmentId: null,
      copyingAppointmentId: null,
      copyBlockedAppointmentId: null,
      stayPhotosAppointment: null,
    };
    this.copyBadgeTimeout = null;
  }

  componentDidUpdate(prevProps, prevState) {
    const wasBlocked = prevState.copyBlockedAppointmentId;
    const isBlocked = this.state.copyBlockedAppointmentId;

    if (!wasBlocked && isBlocked) {
      document.addEventListener('mousedown', this.dismissBlockedPrompt);
    } else if (wasBlocked && !isBlocked) {
      document.removeEventListener('mousedown', this.dismissBlockedPrompt);
    }
  }

  componentWillUnmount() {
    if (this.copyBadgeTimeout) {
      clearTimeout(this.copyBadgeTimeout);
    }
    document.removeEventListener('mousedown', this.dismissBlockedPrompt);
  }

  dismissBlockedPrompt = () => {
    this.setState({ copyBlockedAppointmentId: null });
  };

openStayPhotos = (row) => {
  this.setState({ stayPhotosAppointment: row });
};

closeStayPhotos = () => {
  this.setState({ stayPhotosAppointment: null });
};

  handleStatusFilter = (list, key) => {
    this.props.handleGridFilter(key, list);
  };

  getDisplayStatus = (row) => {
    return getAppointmentDisplayStatus({
      status: row.status,
      phase: row.phase,
      dateTime: row.dateTime,
      typeTitle: row.typeTitle || row.type,
      isBoardingOnly: this.props.isBoardingOnly,
      facilityUtcOffset: this.props.facilityUtcOffset,
    });
  };

  getUpdatesCellContent = (row) => {
    if (
      row.status === appointmentStatus.ongoing ||
      row.status === appointmentStatus.past
    ) {
      return { kind: 'link', label: 'View update history' };
    }
    if (row.status === appointmentStatus.upcoming) {
      return { kind: 'text', label: 'No updates sent yet' };
    }
    if (row.status === appointmentStatus.no_updates) {
      return { kind: 'text', label: 'No updates sent' };
    }
    return { kind: 'text', label: row.status || '' };
  };

  handleCopyHistory = async (event, row) => {
    event.preventDefault();
    event.stopPropagation();

    const { isBoardingOnly } = this.props;
    if (this.state.copyingAppointmentId) {
      return;
    }

    this.setState({
      copyBlockedAppointmentId: null,
      copyingAppointmentId: row.id,
    });

    try {
      const response = await handleRequest(
        'get',
        `appointment/${row.id}/statuses`,
        true,
      );
      const data = response.data;
      const messages = data?.messages || [];
      const baseHeader = buildStatusUpdateArchiveHeader(data);
      const text = buildStatusUpdateArchiveTranscript({
        appointmentType:
          row.typeTitle ||
          row.type ||
          data?.typeTitle ||
          data?.typeOther ||
          '',
        header: {
          ...baseHeader,
          doctor: isBoardingOnly ? '' : row.doctor || baseHeader.doctor,
        },
        messages,
      });

      try {
        await navigator.clipboard.writeText(text);
      } catch (clipboardError) {
        console.error(clipboardError);
        this.setState({
          copyBlockedAppointmentId: row.id,
          copyingAppointmentId: null,
        });
        return;
      }

      if (this.copyBadgeTimeout) {
        clearTimeout(this.copyBadgeTimeout);
      }

      this.setState({
        copiedAppointmentId: row.id,
        copyingAppointmentId: null,
      });

      this.copyBadgeTimeout = setTimeout(() => {
        this.setState({ copiedAppointmentId: null });
        this.copyBadgeTimeout = null;
      }, COPIED_BADGE_DURATION_MS);
    } catch (error) {
      console.error(error);
      this.setState({ copyingAppointmentId: null });
    }
  };

  renderUpdatesMeta = (row) => {
    const typeTitle = String(row.typeTitle || row.type || '')
      .trim()
      .toLowerCase();
    const hidePhotos =
      row.status === appointmentStatus.no_updates &&
      (!this.props.isBoardingOnly ||
        typeTitle === 'daycare' ||
        typeTitle === 'other');
    const photoCount = formatUpdatesMetaCount(row.archiveImageCount ?? 0);
    const photoTitle =
      photoCount === '99+'
        ? `${row.archiveImageCount} photos`
        : `${photoCount} ${photoCount === '1' ? 'photo' : 'photos'}`;

    const showDuration =
      this.props.isBoardingOnly &&
      row.boardingDurationDays != null &&
      Number(row.boardingDurationDays) >= 1;
    const durationCount = showDuration
      ? formatUpdatesMetaCount(row.boardingDurationDays)
      : null;
    const durationTitle = showDuration
      ? durationCount === '99+'
        ? `${row.boardingDurationDays} days`
        : `${durationCount} ${durationCount === '1' ? 'day' : 'days'}`
      : null;

    if (hidePhotos && !showDuration) {
      return null;
    }

    return (
      <span className="appt-updates-meta">
        {!hidePhotos ? (
          Number(row.archiveImageCount) > 0 ? (
            <button
              type="button"
              className="appt-updates-meta__item appt-updates-meta__item--photos"
              title={photoTitle}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                this.openStayPhotos(row);
              }}
            >
              <ImageOutlineIcon
                size={16}
                className="appt-updates-meta__photo-icon"
                aria-hidden
              />
              <span>{photoCount}</span>
            </button>
          ) : (
            <span className="appt-updates-meta__item" title={photoTitle}>
              <ImageOutlineIcon
                size={16}
                className="appt-updates-meta__photo-icon"
                aria-hidden
              />
              <span>{photoCount}</span>
            </span>
          )
        ) : null}
        {showDuration ? (
          <>
            {!hidePhotos ? (
              <span className="appt-updates-meta__sep" aria-hidden>
                ·
              </span>
            ) : null}
            <span className="appt-updates-meta__item" title={durationTitle}>
              <CalendarMonthIcon
                size={16}
                className="appt-updates-meta__calendar-icon"
                aria-hidden
              />
              <span>{durationCount}</span>
            </span>
          </>
        ) : null}
      </span>
    );
  };

  renderUpdatesColumnCell = (row, colKey, cellClass, labels) => {
    const { updatesColumnMode, isBoardingOnly } = this.props;
    const updatesCell = this.getUpdatesCellContent(row);
    const updatesMeta = this.renderUpdatesMeta(row);
    const showCopy =
      updatesColumnMode &&
      !isBoardingOnly &&
      updatesCell.kind === 'link';
    const isCopying = this.state.copyingAppointmentId === row.id;
    const showCopiedBadge = this.state.copiedAppointmentId === row.id;
    const showBlockedPrompt = this.state.copyBlockedAppointmentId === row.id;

    if (updatesCell.kind === 'link') {
      return (
        <td key={colKey} className={cellClass}>
          <span className="appt-updates-cell">
            <span className="appt-updates-cell__main">
              <button
                type="button"
                title={labels.viewStatusHistoryTitle}
                className="nav-link-style"
                onClick={() =>
                  this.props.handleActions(
                    config.appointmentActions.history,
                    row,
                  )
                }
              >
                {updatesCell.label}
              </button>
              {updatesMeta}
            </span>
            {showCopy ? (
              <span className="appt-updates-copy-wrap">
                <button
                  type="button"
                  className="appt-updates-copy"
                  title="Copy history"
                  disabled={isCopying}
                  onClick={(event) => this.handleCopyHistory(event, row)}
                >
                  <ContentCopyIcon size={16} aria-hidden />
                </button>
              </span>
            ) : null}
          </span>
          {showBlockedPrompt ? (
            <CommCopyHistoryBlockedPrompt variant="updateHistory" />
          ) : null}
          {showCopiedBadge ? (
            <span className="appt-updates-copied-badge">Copied!</span>
          ) : null}
        </td>
      );
    }

    return (
      <td key={colKey} className={cellClass}>
        <span className="appt-updates-cell">
          <span className="appt-updates-cell__main">
            <span title={updatesCell.label}>{updatesCell.label}</span>
            {updatesMeta}
          </span>
        </span>
      </td>
    );
  };

  renderPlainHeader = (head, key) => {
    const thClass = head.className ?? '';

    if (head.headerTooltip) {
      return (
        <th key={key} className={thClass}>
          <TableHeaderLabel
            id={`table-header-info-${head.key}`}
            label={head.name}
            tooltip={head.headerTooltip}
            tooltipClassName={head.headerTooltipClassName}
          />
        </th>
      );
    }

    return (
      <th key={key} className={thClass}>
        <p>{head.name}</p>
      </th>
    );
  };

  render() {
    const {
      rows,
      heads,
      sortField,
      sortOrder,
      stickyTop,
      overflowX = true,
      showActions = true,
      updatesColumnMode = false,
    } = this.props;
    const labels = getAppointmentLabels(!!this.props.isBoardingOnly);

    const tableRows = rows.map((row, key) => {
      const displayStatus = this.getDisplayStatus(row);

      const _row = heads.map((columnConfig, colKey) => {
        const value = columnConfig.key;
        if (!columnConfig) return true;

        const skipCapitalize = updatesColumnMode && value === 'status';
        const capitalizeClass =
          columnConfig.capitalize && !skipCapitalize ? 'text-capitalize' : '';
        const link = columnConfig.link?.(row);
        const cellClass = classNames(
          'custom-no-over-flow-td',
          columnConfig.className,
          capitalizeClass,
        );

        if (value === 'endDate') {
          const endDateDisplay = getEndDateCellDisplay(row);
          return (
            <td key={colKey} title={endDateDisplay} className={cellClass}>
              {endDateDisplay}
            </td>
          );
        }

        if (value === 'time' && row.createdFromTrackingBoard) {
          return (
            <td key={colKey} title={'\u2014'} className={cellClass}>
              {'\u2014'}
            </td>
          );
        }

        if (value === 'status' && updatesColumnMode) {
          return this.renderUpdatesColumnCell(row, colKey, cellClass, labels);
        }

        if (
          value === 'status' &&
          (row.status === appointmentStatus.past ||
            row.status === appointmentStatus.ongoing)
        ) {
          return (
            <td key={colKey} className={cellClass}>
              <button
                type="button"
                title={labels.viewStatusHistoryTitle}
                className="nav-link-style"
                onClick={() =>
                  this.props.handleActions(
                    config.appointmentActions.history,
                    row,
                  )
                }
              >
                {displayStatus}
              </button>
            </td>
          );
        }

        if (value === 'status') {
          return (
            <td key={colKey} title={displayStatus} className={cellClass}>
              {displayStatus}
            </td>
          );
        }

        return (
          <td key={colKey} title={row[value]} className={cellClass}>
            {link ? <Link to={link}>{row[value]}</Link> : row[value]}
          </td>
        );
      });

      const pawIconClassName = `six-layers ${row.status.replace(/\s+/g, '-')}`;

      return (
        <tr className="body-tr" key={key}>
          {_row}
          {showActions ? (
            <td className="appt-action-column">
              <UncontrolledDropdown>
                <DropdownToggle>
                  <span className={pawIconClassName} />
                </DropdownToggle>
                <DropdownMenu className="dropdown__menu">
                  {row.status === appointmentStatus.upcoming ? (
                    <>
                      <DropdownItem tag="div">
                        <a
                          onClick={() =>
                            this.props.handleActions(
                              config.appointmentActions.reschedule,
                              row,
                            )
                          }
                        >
                          Reschedule
                        </a>
                      </DropdownItem>
                      <DropdownItem tag="div">
                        <a
                          onClick={() =>
                            this.props.handleActions(
                              config.appointmentActions.cancel,
                              row.id,
                            )
                          }
                        >
                          Cancel
                        </a>
                      </DropdownItem>
                    </>
                  ) : (
                    <DropdownItem tag="div">
                      <a
                        onClick={() =>
                          this.props.handleActions(
                            config.appointmentActions.remove,
                            row.id,
                          )
                        }
                      >
                        Remove
                      </a>
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </UncontrolledDropdown>
            </td>
          ) : null}
        </tr>
      );
    });

    return (
      <>
      <div className="row">
        <div className="col-md-12">
          <div
            style={
              overflowX
                ? { overflowX: 'auto', width: '100%' }
                : { width: '100%' }
            }
          >
            <Table striped className="custom-table loyal-table fixed-layout">
              <thead>
                <tr
                  className="header-tr"
                  style={stickyTop ? { top: stickyTop } : {}}
                >
                  {heads.map((head, key) => {
                    if (!head) return true;
                    const thClass = head.className ?? '';
                    const _sortField = head.key;
                    return head.sortable && !head.filterable ? (
                      <th key={key} className={thClass}>
                        {sortField === head.key ? (
                          <SortingField
                            icon={
                              sortOrder === 1
                                ? 'triangle-up'
                                : 'triangle-down'
                            }
                            name={head.name}
                            onSort={() =>
                              this.props.handleGridSort(_sortField)
                            }
                          />
                        ) : (
                          <SortingField
                            name={head.name}
                            icon="diamond-narrow"
                            onSort={() =>
                              this.props.handleGridSort(_sortField)
                            }
                          />
                        )}
                      </th>
                    ) : head.filterable &&
                      ['status', 'type', 'typeTitle', 'petName'].includes(
                        head.key,
                      ) ? (
                      <th key={key} className={thClass}>
                        <TableStatusFilterComponent
                          allFilters={head.filterValue}
                          action={(list) => {
                            this.handleStatusFilter(list, head.key);
                          }}
                          label={head.name}
                        />
                      </th>
                    ) : (
                      this.renderPlainHeader(head, key)
                    );
                  })}
                  {showActions ? (
                    <th className="text-center action appt-action-column">
                      <p className="action">Action</p>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>{tableRows}</tbody>
            </Table>
          </div>
          {rows.length === 0 ? (
            <div className="no-data-message">
              {this.props.labels?.noAppointments ?? 'No Appointments'}
            </div>
          ) : null}
        </div>
      </div>
      <StayPhotosModal
        isOpen={!!this.state.stayPhotosAppointment}
        onClose={this.closeStayPhotos}
        appointment={this.state.stayPhotosAppointment}
        isBoardingOnly={!!this.props.isBoardingOnly}
      />
      </>
    );
  }
}
